import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraCombobox, SpecraEntityCell, SpecraFilterToolbar, SpecraSelect, SpecraTabBar } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { format } from 'date-fns';
import { useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { ContractVehicle } from '../../../../../../gen/ts/mono/datagateway/v1/delivery_pb';
import { rateBookHref, SharedEditNotice } from '../../../components/SharedRecord';
import { useTenantAreas } from '../../../lib/access/useTenantAreas';
import { holderVariant } from '../../Registries/badgeVariants';
import { RegistryListNote } from '../../Registries/list/RegistryListNote';
import { OpenFullPage, useRowOpen } from '../../Registries/list/RowOpen';
import { useVehicleSummaries } from '../../Registries/list/useRegistrySummaries';
import { useShowMore } from '../../Registries/list/useShowMore';
import { MergedRecordBanner } from '../../Registries/merge/MergedRecordBanner';
import { MergeRecordDialog } from '../../Registries/merge/MergeRecordDialog';
import { useGoToMergedSurvivor, useMergedRecordRedirect } from '../../Registries/merge/useMerge';
import { useRegistryRecordsEnabled } from '../../Registries/registryFlags';
import { registryHref } from '../../Registries/registryHref';
import { EntityTable } from '../_shared/components/EntityTable';
import { useGovernmentOrgCatalog } from '../Programs/referenceDataHooks';
import { agencyLabel, dateInput, editVehicleProfile, orderingEligibilityLabel, vehicleTypeLabel, vehicleTypes } from './contractVehicleUi';
import { useRegistryQuery, useRegistryRecord } from './registryParams';
import { VehicleConnectionsSection } from './VehicleConnectionsSection';
import { VehicleProfileEditor } from './VehicleProfileEditor';
import { VehicleRelationshipsSection } from './VehicleRelationshipsSection';
import { useVehicleBrowse, useVehicleProfile, useVehicleViewer, useVehicleWrites } from './vehicleIdentityHooks';
/** "ACTIVE" → "Active"; empty → "Unknown". */ export const vehicleStatusLabel = (status)=>status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : 'Unknown';
export function ContractVehiclesTab() {
    const viewer = useVehicleViewer();
    return /*#__PURE__*/ _jsx(VehicleWorkspace, {}, `${viewer.tenant}:${viewer.user}`);
}
function VehicleWorkspace() {
    const recordsEnabled = useRegistryRecordsEnabled();
    const rowOpen = useRowOpen('VEHICLE');
    const canEdit = useTenantAreas().registries;
    const [params, setParams] = useSearchParams();
    const [query, setQuery] = useRegistryQuery();
    const { recordId, openRecord, closeRecord } = useRegistryRecord();
    const [creating, setCreating] = useState(false);
    const catalog = useGovernmentOrgCatalog();
    const type = params.get('vehicleType') || '';
    const agencyId = params.get('agency') || '';
    const includeInactive = params.get('history') === 'true';
    const browse = useVehicleBrowse({
        query,
        vehicleType: type,
        agencyId,
        includeInactive
    });
    const window = useShowMore(browse.data ?? [], `${query}:${type}:${agencyId}:${includeInactive}`);
    const visible = recordsEnabled ? window.visible : browse.data ?? [];
    const summaries = useVehicleSummaries(recordsEnabled && !recordId ? visible.map((v)=>v.id) : []);
    const summaryById = new Map((summaries.isError ? [] : summaries.data ?? []).map((s)=>[
            s.vehicleId,
            s
        ]));
    const columns = [
        {
            key: 'name',
            header: 'Vehicle',
            render: (v)=>/*#__PURE__*/ _jsx(SpecraEntityCell, {
                    title: v.name,
                    description: vehicleTypeLabel(v.vehicleType)
                })
        },
        {
            key: 'agency',
            header: 'Awarded by',
            render: (v)=>summaryById.get(v.id)?.awardingOrg?.name || '—'
        },
        {
            key: 'eligibility',
            header: 'Who can order',
            render: (v)=>{
                const s = summaryById.get(v.id);
                return s ? orderingEligibilityLabel(new ContractVehicle({
                    orderingEligibility: s.orderingEligibility,
                    agencyAcronym: v.agencyAcronym,
                    legacySourceAgency: v.legacySourceAgency
                })) : '—';
            }
        },
        {
            key: 'us',
            header: 'Us',
            render: (v)=>{
                const status = summaryById.get(v.id)?.ourStatus;
                if (!status?.kind) return '—';
                const label = status.kind === 'HOLDER' ? 'Holder' : status.kind === 'THROUGH' ? `Through ${status.viaCompany?.name || '—'}` : 'Not on it';
                return /*#__PURE__*/ _jsx(SpecraBadge, {
                    variant: holderVariant(status.kind),
                    children: label
                });
            }
        },
        {
            key: 'prices',
            header: 'Price lists on file',
            render: (v)=>{
                const generation = summaryById.get(v.id)?.latestGeneration;
                return generation ? /*#__PURE__*/ _jsx(SpecraEntityCell, {
                    title: generation.title,
                    description: `${generation.companiesWithCeilings} companies`
                }) : '—';
            }
        },
        {
            key: 'ordering',
            header: 'Ordering',
            render: (v)=>{
                const s = summaryById.get(v.id);
                if (!s?.orderingEnd) return '—';
                return s.orderingStart ? `${format(s.orderingStart.toDate(), 'yyyy')}–${format(s.orderingEnd.toDate(), 'yyyy')}` : `ends ${format(s.orderingEnd.toDate(), 'MMM yyyy')}`;
            }
        },
        {
            key: 'status',
            header: 'Status',
            render: (v)=>vehicleStatusLabel(v.status)
        }
    ];
    const filter = (key, value)=>setParams((previous)=>{
            const next = new URLSearchParams(previous);
            if (value) next.set(key, value);
            else next.delete(key);
            return next;
        }, {
            replace: true
        });
    return /*#__PURE__*/ _jsx("div", {
        className: "space-y-4",
        children: recordId ? /*#__PURE__*/ _jsx(VehicleRecord, {
            id: recordId,
            onClose: closeRecord
        }, recordId) : /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(SpecraFilterToolbar, {
                    search: {
                        value: query,
                        onChange: (e)=>setQuery(e.target.value),
                        placeholder: 'Search by name or alias',
                        'aria-label': 'Search vehicles by name or alias',
                        ...{
                            'data-testid': Testids.vehicleIdentity.search
                        }
                    },
                    filters: /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsxs(SpecraSelect, {
                                size: "sm",
                                "aria-label": "Vehicle type",
                                "data-testid": Testids.vehicleIdentity.typeFilter,
                                value: type,
                                onChange: (e)=>filter('vehicleType', e.target.value),
                                children: [
                                    /*#__PURE__*/ _jsx("option", {
                                        value: "",
                                        children: "Type: any"
                                    }),
                                    vehicleTypes.map((t)=>/*#__PURE__*/ _jsx("option", {
                                            value: t,
                                            children: vehicleTypeLabel(t)
                                        }, t))
                                ]
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "w-64",
                                "data-testid": Testids.deliveryRegistries.vehicleAgencyOrgPicker,
                                children: /*#__PURE__*/ _jsx(SpecraCombobox, {
                                    label: "Awarded by",
                                    placeholder: "Awarded by: any",
                                    searchPlaceholder: "Search agencies and offices",
                                    clearable: true,
                                    clearLabel: "Any agency",
                                    value: agencyId || undefined,
                                    onValueChange: (v)=>filter('agency', v ?? ''),
                                    loading: catalog.isPending,
                                    fallbackLabel: catalog.byId.get(agencyId)?.name,
                                    options: (catalog.data ?? []).map((o)=>({
                                            value: o.id,
                                            label: o.acronym ? `${o.name} (${o.acronym})` : o.name,
                                            description: o.displayPath || undefined,
                                            keywords: [
                                                o.acronym,
                                                o.displayPath
                                            ].filter(Boolean)
                                        }))
                                })
                            }),
                            /*#__PURE__*/ _jsxs(SpecraSelect, {
                                size: "sm",
                                "aria-label": "Vehicle history",
                                "data-testid": Testids.vehicleIdentity.historyFilter,
                                value: includeInactive ? 'true' : '',
                                onChange: (e)=>filter('history', e.target.value),
                                children: [
                                    /*#__PURE__*/ _jsx("option", {
                                        value: "",
                                        children: "Active"
                                    }),
                                    /*#__PURE__*/ _jsx("option", {
                                        value: "true",
                                        children: "Active and archived"
                                    })
                                ]
                            })
                        ]
                    }),
                    resultSummary: !browse.isPending && !browse.isError ? `${browse.data?.length ?? 0} ${(browse.data?.length ?? 0) === 1 ? 'vehicle' : 'vehicles'}` : undefined,
                    actions: canEdit && /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.deliveryRegistries.vehicleCreate,
                        onClick: ()=>setCreating(true),
                        children: "New Contract Vehicle"
                    })
                }),
                creating && canEdit && /*#__PURE__*/ _jsx(VehicleProfileEditor, {
                    onCancel: ()=>setCreating(false),
                    onSaved: (p)=>{
                        setCreating(false);
                        if (p.vehicle) openRecord(p.vehicle.id);
                    }
                }),
                browse.isError ? /*#__PURE__*/ _jsxs("div", {
                    role: "alert",
                    children: [
                        "Could not load Vehicles.",
                        ' ',
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            onClick: ()=>void browse.refetch(),
                            children: "Retry vehicles"
                        })
                    ]
                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(EntityTable, {
                            data: visible,
                            columns: recordsEnabled ? columns : [
                                {
                                    key: 'name',
                                    header: 'Name'
                                },
                                {
                                    key: 'vehicleType',
                                    header: 'Type',
                                    render: (v)=>vehicleTypeLabel(v.vehicleType)
                                },
                                {
                                    key: 'awardingAgencyOrgId',
                                    header: 'Agency',
                                    render: (v)=>agencyLabel(v, catalog.byId)
                                },
                                {
                                    key: 'contractNumber',
                                    header: 'Mechanism identifier'
                                },
                                {
                                    key: 'status',
                                    header: 'Status',
                                    render: (v)=>v.status || 'Unknown'
                                }
                            ],
                            rowKey: (v)=>v.id,
                            onRowClick: (v)=>rowOpen.onRowClick(v.id),
                            isLoading: browse.isPending,
                            hideRowActions: !recordsEnabled,
                            rowActions: (v)=>rowOpen.quickLookAction(v.id),
                            testid: Testids.deliveryRegistries.vehiclesTable,
                            rowTestid: Testids.deliveryRegistries.vehicleRow
                        }),
                        recordsEnabled && window.hasMore && /*#__PURE__*/ _jsx(SpecraButton, {
                            variant: "secondary",
                            "data-testid": Testids.deliveryRegistries.showMore,
                            onClick: window.showMore,
                            children: "Show more"
                        }),
                        recordsEnabled && /*#__PURE__*/ _jsx(RegistryListNote, {
                            children: "Who awarded it, who may order on it, and who actually ordered are kept as separate facts."
                        })
                    ]
                })
            ]
        })
    });
}
const VEHICLE_MERGE_FIELDS = [
    {
        id: 'contractNumber',
        label: 'Contract number',
        strong: true
    },
    {
        id: 'type',
        label: 'Type'
    },
    {
        id: 'status',
        label: 'Status'
    }
];
const vehicleMergeSide = (v)=>({
        id: v.id,
        name: v.name,
        fields: {
            contractNumber: v.contractNumber,
            type: vehicleTypeLabel(v.vehicleType),
            status: v.status
        }
    });
// Mounted only while merging, so the survivor list is fetched on demand.
function VehicleMergeDialog({ vehicle, onOpenChange, onMerged }) {
    const browse = useVehicleBrowse({});
    return /*#__PURE__*/ _jsx(MergeRecordDialog, {
        kind: "VEHICLE",
        open: true,
        onOpenChange: onOpenChange,
        loser: vehicleMergeSide(vehicle),
        candidates: (browse.data ?? []).filter((c)=>c.id !== vehicle.id).map(vehicleMergeSide),
        fieldDefs: VEHICLE_MERGE_FIELDS,
        onMerged: onMerged
    });
}
function VehicleRecord({ id, onClose }) {
    const profile = useVehicleProfile(id);
    const canEdit = useTenantAreas().registries;
    const catalog = useGovernmentOrgCatalog();
    const [editing, setEditing] = useState(false);
    const [params, setParams] = useSearchParams();
    const section = [
        'overview',
        'companies',
        'labor',
        'work'
    ].includes(params.get('vehicleSection') || '') ? params.get('vehicleSection') || 'overview' : 'overview';
    const [archiveConfirm, setArchiveConfirm] = useState(false);
    const writes = useVehicleWrites();
    const location = useLocation();
    const origin = location.pathname + location.search;
    const goToSurvivor = useGoToMergedSurvivor();
    const [merging, setMerging] = useState(false);
    // A loser's profile still loads (with its merge pointer) or fails; either way its
    // URL lands on the survivor. Only a record with no merge pointer is "unavailable".
    const redirect = useMergedRecordRedirect('VEHICLE', id, profile.isError || !!profile.data?.vehicle?.mergedIntoId);
    if (profile.isPending || redirect.resolving) return /*#__PURE__*/ _jsx("output", {
        children: "Loading Vehicle profile…"
    });
    if (profile.isError || !profile.data?.vehicle) return /*#__PURE__*/ _jsxs("div", {
        role: "alert",
        children: [
            "Vehicle unavailable.",
            ' ',
            /*#__PURE__*/ _jsx(SpecraButton, {
                onClick: ()=>void profile.refetch(),
                children: "Retry profile"
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                onClick: onClose,
                children: "Close record"
            })
        ]
    });
    const v = profile.data.vehicle;
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-4",
        "data-testid": Testids.vehicleIdentity.profile,
        children: [
            /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "secondary",
                onClick: onClose,
                children: "Back to vehicles"
            }),
            /*#__PURE__*/ _jsx(OpenFullPage, {
                kind: "VEHICLE",
                id: id
            }),
            /*#__PURE__*/ _jsx(MergedRecordBanner, {
                kind: "VEHICLE",
                survivorId: id
            }),
            /*#__PURE__*/ _jsx("h2", {
                className: "font-semibold",
                children: v.name
            }),
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    vehicleTypeLabel(v.vehicleType),
                    " · ",
                    v.status || 'Unknown status',
                    " ·",
                    ' ',
                    v.contractNumber || 'Mechanism identifier unknown'
                ]
            }),
            /*#__PURE__*/ _jsx("p", {
                children: agencyLabel(v, catalog.byId)
            }),
            v.awardingAgencyOrgId && /*#__PURE__*/ _jsx(Link, {
                className: "text-primary underline",
                to: registryHref({
                    kind: 'GOVERNMENT_ORG',
                    id: v.awardingAgencyOrgId
                }, origin) ?? '/registries?tab=govorgs',
                children: "Inspect agency hierarchy"
            }),
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    "Ordering period: ",
                    dateInput(v.orderingPeriodStart) || 'Unknown',
                    " –",
                    ' ',
                    dateInput(v.orderingPeriodEnd) || 'Unknown'
                ]
            }),
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    "NAICS: ",
                    v.naicsCode || 'Unknown'
                ]
            }),
            /*#__PURE__*/ _jsx("p", {
                children: v.description || 'Description unknown'
            }),
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    "Aliases: ",
                    profile.data.aliases.join(' · ') || 'None recorded'
                ]
            }),
            v.legacySourceAgency && v.awardingAgencyOrgId && /*#__PURE__*/ _jsxs("p", {
                children: [
                    "Original source agency: ",
                    v.legacySourceAgency
                ]
            }),
            canEdit && !v.isSystemSeed && !editing && /*#__PURE__*/ _jsxs("div", {
                className: "flex gap-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.deliveryRegistries.vehicleEdit,
                        onClick: ()=>setEditing(true),
                        children: "Edit profile"
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        "data-testid": Testids.registryMerge.open,
                        onClick: ()=>setMerging(true),
                        children: "Merge into…"
                    }),
                    ![
                        'INACTIVE',
                        'ARCHIVED'
                    ].includes(v.status) && /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        "data-testid": Testids.deliveryRegistries.vehicleDelete,
                        onClick: ()=>setArchiveConfirm(true),
                        children: "Archive Vehicle"
                    })
                ]
            }),
            v.isSystemSeed && /*#__PURE__*/ _jsx("p", {
                children: "System reference profile is read-only. Company relationships remain separately maintained."
            }),
            editing && canEdit && !v.isSystemSeed && /*#__PURE__*/ _jsx(VehicleProfileEditor, {
                profile: profile.data,
                onSaved: ()=>setEditing(false),
                onCancel: ()=>setEditing(false),
                onReload: async ()=>{
                    await profile.refetch();
                    setEditing(false);
                }
            }, String(v.revision)),
            merging && /*#__PURE__*/ _jsx(VehicleMergeDialog, {
                vehicle: v,
                onOpenChange: setMerging,
                onMerged: goToSurvivor
            }),
            archiveConfirm && /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        children: "Archive this mechanism? Historical references remain available; it will be excluded from ordinary new selections."
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        disabled: writes.profile.isPending,
                        onClick: async ()=>{
                            try {
                                await writes.profile.mutateAsync(editVehicleProfile(profile.data, {
                                    status: 'INACTIVE'
                                }));
                                setArchiveConfirm(false);
                            } catch  {
                            /* retain error */ }
                        },
                        children: "Confirm archive Vehicle"
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        onClick: ()=>setArchiveConfirm(false),
                        children: "Cancel archive"
                    })
                ]
            }),
            writes.profile.error && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                children: "Archive could not be confirmed. Reload the profile to reconcile before retrying."
            }),
            /*#__PURE__*/ _jsx(SpecraTabBar, {
                label: "Vehicle record sections",
                value: section,
                onValueChange: (value)=>setParams((previous)=>{
                        const next = new URLSearchParams(previous);
                        next.set('vehicleSection', value);
                        return next;
                    }),
                options: [
                    {
                        value: 'overview',
                        label: 'Overview'
                    },
                    {
                        value: 'companies',
                        label: 'Companies'
                    },
                    {
                        value: 'labor',
                        label: 'Labor & Rates'
                    },
                    {
                        value: 'work',
                        label: 'Connected Work'
                    }
                ]
            }),
            section === 'overview' && /*#__PURE__*/ _jsx(SharedEditNotice, {
                record: "Contract Vehicle",
                scope: "Companies, source evidence and connected work"
            }),
            section === 'companies' && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(VehicleRelationshipsSection, {
                        vehicleId: id
                    }),
                    /*#__PURE__*/ _jsx(VehicleConnectionsSection, {
                        vehicleId: id,
                        kind: "association"
                    })
                ]
            }),
            section === 'labor' && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(VehicleConnectionsSection, {
                        vehicleId: id,
                        kind: "labor"
                    }),
                    /*#__PURE__*/ _jsx(VehicleConnectionsSection, {
                        vehicleId: id,
                        kind: "rate"
                    }),
                    /*#__PURE__*/ _jsx(VehicleConnectionsSection, {
                        vehicleId: id,
                        kind: "source"
                    }),
                    /*#__PURE__*/ _jsx(Link, {
                        to: rateBookHref({
                            vehicle: id
                        }),
                        className: "inline-block text-xs text-primary underline-offset-2 hover:underline",
                        "data-testid": Testids.deliveryRegistries.rateBookOpen,
                        children: "Open in Rate Book"
                    })
                ]
            }),
            section === 'work' && /*#__PURE__*/ _jsx(VehicleConnectionsSection, {
                vehicleId: id,
                kind: "program"
            })
        ]
    });
}
